import { clampPageSize, type SortOrder, type TableSnapshot } from "./offset-list-params";

/**
 * Query-string keys the table owns. Anything else in the URL is left untouched,
 * so a page can keep its own params (tab, entity id, …) alongside table state.
 */
export const TABLE_URL_KEYS = {
	order: "order",
	page: "page",
	pageSize: "pageSize",
	q: "q",
	sort: "sort",
} as const;

/** Page numbers are 1-based in the URL, 0-based in the table. */
const FIRST_PAGE_NUMBER = 1;
const DEFAULT_SORT_ORDER: SortOrder = "asc";

export type TableUrlDefaults = { pageSize: number };

function toSortOrder(raw: string | null): SortOrder {
	switch (raw) {
		case "asc":
			return "asc";
		case "desc":
			return "desc";
		default:
			return DEFAULT_SORT_ORDER;
	}
}

function toPageNumber(raw: string | null): number | undefined {
	if (raw === null) return undefined;
	const n = Number(raw);
	if (!Number.isInteger(n) || n < FIRST_PAGE_NUMBER) return undefined;
	return n;
}

function toPageSize(raw: string | null, fallback: number): number {
	if (raw === null) return fallback;
	const n = Number(raw);
	if (!Number.isInteger(n) || n <= 0) return fallback;
	return clampPageSize(n);
}

/** Reads a table snapshot out of a `location.search` string. */
export function parseTableUrlState(search: string, defaults: TableUrlDefaults): TableSnapshot {
	const params = new URLSearchParams(search);
	const sortBy = params.get(TABLE_URL_KEYS.sort);
	const page = toPageNumber(params.get(TABLE_URL_KEYS.page));

	return {
		pageIndex: page === undefined ? 0 : page - FIRST_PAGE_NUMBER,
		pageSize: toPageSize(params.get(TABLE_URL_KEYS.pageSize), defaults.pageSize),
		search: params.get(TABLE_URL_KEYS.q) ?? "",
		sort: sortBy
			? { desc: toSortOrder(params.get(TABLE_URL_KEYS.order)) === "desc", id: sortBy }
			: undefined,
	};
}

/**
 * Renders a snapshot back into a query string, preserving params the table does
 * not own and omitting every value that equals its default, so the untouched
 * default view keeps a clean URL.
 */
export function tableUrlSearch(
	currentSearch: string,
	snapshot: TableSnapshot,
	defaults: TableUrlDefaults,
): string {
	const params = new URLSearchParams(currentSearch);
	for (const key of Object.values(TABLE_URL_KEYS)) params.delete(key);

	const q = snapshot.search.trim();
	if (snapshot.pageIndex > 0) {
		params.set(TABLE_URL_KEYS.page, `${snapshot.pageIndex + FIRST_PAGE_NUMBER}`);
	}
	if (snapshot.pageSize !== defaults.pageSize) {
		params.set(TABLE_URL_KEYS.pageSize, `${snapshot.pageSize}`);
	}
	if (snapshot.sort) {
		params.set(TABLE_URL_KEYS.sort, snapshot.sort.id);
		params.set(TABLE_URL_KEYS.order, snapshot.sort.desc ? "desc" : "asc");
	}
	if (q) params.set(TABLE_URL_KEYS.q, q);

	return params.toString();
}
